import { getStoredAuthToken } from "@/utils/authStorage";
import { DEFAULT_PLAYLIST_IMAGE_PATH } from "@/utils/imageUtils";
import {
    getRetryAfterSecondsFromHeaders,
    isRateLimitCoolingDown,
    setRateLimitCooldown,
} from "@/utils/rateLimit";

const RECENT_JOINS_RATE_LIMIT_KEY = "sessions:recent-joined-playlists";

export interface RecentlyJoinedPlaylistRecord {
    id: string;
    playlistId?: string;
    hostId?: string;
    hostName?: string;
    hostAvatar?: string;
    title: string;
    image: string;
    joinedAt: string;
    source?: "session" | "playlist";
    isOnline?: boolean;
    songCount?: number;
}

interface RecentlyJoinedPlaylistsApiResponse {
    items?: Array<{
        session_id?: string;
        playlist_id?: string;
        id?: string;
        title?: string;
        playlist_title?: string;
        name?: string;
        host_id?: string;
        host_name?: string;
        host_avatar?: string;
        image?: string | null;
        joined_at?: string;
        joinedAt?: string;
        is_online?: boolean;
        song_count?: number;
    }>;
}

const STORAGE_KEY = "qit:recently-joined-playlists";
export const RECENTLY_JOINED_PLAYLISTS_EVENT = "qit:recently-joined-playlists-updated";
const FALLBACK_SESSION_TITLE_PATTERN = /^(.+'s Session|Live Session|Untitled Session)$/i;

const normalizeText = (value: unknown, fallback = ""): string => {
    if (typeof value !== "string") return fallback;
    const trimmed = value.trim();
    return trimmed || fallback;
};

const parseStoredJson = (key: string): Record<string, unknown> | null => {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
    } catch {
        return null;
    }
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
    if (!token) return null;
    try {
        const [, rawPayload] = token.split(".");
        if (!rawPayload) return null;
        const normalized = rawPayload.replace(/-/g, "+").replace(/_/g, "/");
        const paddingLength = (4 - (normalized.length % 4)) % 4;
        const padded = normalized.padEnd(normalized.length + paddingLength, "=");
        const payload = JSON.parse(atob(padded));
        return payload && typeof payload === "object" ? (payload as Record<string, unknown>) : null;
    } catch {
        return null;
    }
};

const resolveUserIdFromToken = (token: string): string => {
    const payload = decodeJwtPayload(token);
    return normalizeText(
        payload?.user_id ??
        payload?.id ??
        (typeof payload?.sub === "string" && !payload.sub.includes("@")
            ? payload.sub
            : ""),
        ""
    );
};

const getAccountScopedStorageKey = (): string => {
    if (typeof window === "undefined") return STORAGE_KEY;

    const token = getStoredAuthToken();
    const storedUser = parseStoredJson("user");
    const tokenUserId = resolveUserIdFromToken(token);
    const userId = token
        ? normalizeText(storedUser?.id ?? storedUser?.user_id ?? storedUser?.userId ?? tokenUserId, "")
        : "";
    if (userId) {
        return `${STORAGE_KEY}:user:${userId}`;
    }

    const storedGuest = parseStoredJson("guestUser");
    const guestId = normalizeText(
        storedGuest?.id ?? storedGuest?.user_id ?? storedGuest?.userId,
        ""
    );
    if (guestId) {
        return `${STORAGE_KEY}:guest:${guestId}`;
    }

    return `${STORAGE_KEY}:anonymous`;
};

const readRecordsFromStorage = (key: string): RecentlyJoinedPlaylistRecord[] => {
    if (typeof window === "undefined") return [];
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];

    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];

        const normalized = parsed
            .map((item) => normalizeRecord(item))
            .filter((item): item is RecentlyJoinedPlaylistRecord => Boolean(item));

        return sortByMostRecent(normalized);
    } catch {
        return [];
    }
};

const writeRecordsToStorage = (records: RecentlyJoinedPlaylistRecord[]) => {
    if (typeof window === "undefined") return;
    const scopedKey = getAccountScopedStorageKey();
    window.localStorage.setItem(scopedKey, JSON.stringify(records));
};

const normalizeDateString = (value: unknown): string => {
    const raw = normalizeText(value);
    if (!raw) return new Date().toISOString();
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return new Date().toISOString();
    return parsed.toISOString();
};

const isLikelyIdentifier = (value: string): boolean => {
    if (!value) return false;
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)) {
        return true;
    }
    if (/^(usr|user|guest|ven|session|sid|id|playlist|plist|plst)_[a-z0-9]+$/i.test(value)) {
        return true;
    }
    return /^[a-f0-9]{24,}$/i.test(value);
};

const isFallbackSessionTitle = (value: string): boolean => {
    const normalized = normalizeText(value);
    if (!normalized) return true;
    return FALLBACK_SESSION_TITLE_PATTERN.test(normalized);
};

const choosePreferredTitle = (primaryTitle: unknown, secondaryTitle: unknown): string => {
    const primary = normalizeText(primaryTitle);
    const secondary = normalizeText(secondaryTitle);

    if (primary && !isLikelyIdentifier(primary) && !isFallbackSessionTitle(primary)) return primary;
    if (secondary && !isLikelyIdentifier(secondary) && !isFallbackSessionTitle(secondary)) return secondary;
    if (primary && !isLikelyIdentifier(primary)) return primary;
    if (secondary && !isLikelyIdentifier(secondary)) return secondary;
    return primary || secondary || "Untitled Playlist";
};

const toTimestamp = (value: string): number => {
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? 0 : time;
};

const normalizeRecord = (
    raw: Partial<RecentlyJoinedPlaylistRecord>
): RecentlyJoinedPlaylistRecord | null => {
    const id = normalizeText(raw.id);
    const title = normalizeText(raw.title, "Untitled Playlist");
    if (!id) return null;

    return {
        id,
        playlistId: normalizeText(raw.playlistId),
        hostId: normalizeText(raw.hostId),
        hostName: normalizeText(raw.hostName),
        hostAvatar: normalizeText(raw.hostAvatar),
        title,
        image: normalizeText(raw.image, DEFAULT_PLAYLIST_IMAGE_PATH),
        joinedAt: normalizeDateString(raw.joinedAt),
        source: raw.source === "session" || raw.source === "playlist" ? raw.source : undefined,
        isOnline: typeof raw.isOnline === "boolean" ? raw.isOnline : undefined,
        songCount: typeof raw.songCount === "number" && Number.isFinite(raw.songCount)
            ? Math.max(0, Math.floor(raw.songCount))
            : undefined,
    };
};

const sortByMostRecent = (items: RecentlyJoinedPlaylistRecord[]): RecentlyJoinedPlaylistRecord[] =>
    [...items].sort((a, b) => {
        const aTime = new Date(a.joinedAt).getTime();
        const bTime = new Date(b.joinedAt).getTime();
        return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime);
    });

const emitUpdateEvent = (playlists: RecentlyJoinedPlaylistRecord[]) => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
        new CustomEvent(RECENTLY_JOINED_PLAYLISTS_EVENT, {
            detail: { playlists },
        })
    );
};

export const setRecentlyJoinedPlaylists = (
    records: Array<Partial<RecentlyJoinedPlaylistRecord>>
): RecentlyJoinedPlaylistRecord[] => {
    if (typeof window === "undefined") return [];

    const normalized = records
        .map((item) => normalizeRecord(item))
        .filter((item): item is RecentlyJoinedPlaylistRecord => Boolean(item));
    const next = sortByMostRecent(normalized);

    writeRecordsToStorage(next);
    emitUpdateEvent(next);
    return next;
};

export const getRecentlyJoinedPlaylists = (): RecentlyJoinedPlaylistRecord[] => {
    if (typeof window === "undefined") return [];
    const scopedKey = getAccountScopedStorageKey();
    const scopedRecords = readRecordsFromStorage(scopedKey);
    if (scopedRecords.length > 0) {
        return scopedRecords;
    }

    // One-time compatibility path: migrate legacy unscope key to current account scope.
    const legacyRecords = readRecordsFromStorage(STORAGE_KEY);
    if (legacyRecords.length > 0) {
        writeRecordsToStorage(legacyRecords);
        return legacyRecords;
    }

    return [];
};

export const addRecentlyJoinedPlaylist = (
    record: Partial<RecentlyJoinedPlaylistRecord>
): RecentlyJoinedPlaylistRecord[] => {
    if (typeof window === "undefined") return [];

    const normalized = normalizeRecord(record);
    if (!normalized) return getRecentlyJoinedPlaylists();

    const existing = getRecentlyJoinedPlaylists();
    const deduped = existing.filter((item) => item.id !== normalized.id);
    const next = sortByMostRecent([normalized, ...deduped]);

    writeRecordsToStorage(next);
    emitUpdateEvent(next);

    return next;
};

export const removeRecentlyJoinedPlaylist = (
    id: string
): RecentlyJoinedPlaylistRecord[] => {
    if (typeof window === "undefined") return [];
    const normalizedId = normalizeText(id);
    if (!normalizedId) return getRecentlyJoinedPlaylists();

    const next = getRecentlyJoinedPlaylists().filter((item) => item.id !== normalizedId);
    writeRecordsToStorage(next);
    emitUpdateEvent(next);
    return next;
};

export const markRecentlyJoinedPlaylistOffline = (
    id: string
): RecentlyJoinedPlaylistRecord[] => {
    if (typeof window === "undefined") return [];
    const normalizedId = normalizeText(id);
    if (!normalizedId) return getRecentlyJoinedPlaylists();

    const next = getRecentlyJoinedPlaylists().map((item) => {
        if (item.id !== normalizedId) return item;
        return {
            ...item,
            isOnline: false,
        };
    });
    writeRecordsToStorage(next);
    emitUpdateEvent(next);
    return next;
};

export const mergeRecentlyJoinedPlaylists = (
    primary: RecentlyJoinedPlaylistRecord[],
    secondary: RecentlyJoinedPlaylistRecord[]
): RecentlyJoinedPlaylistRecord[] => {
    const merged = new Map<string, RecentlyJoinedPlaylistRecord>();
    const localById = new Map<string, RecentlyJoinedPlaylistRecord>();

    secondary.forEach((item) => {
        const normalized = normalizeRecord(item);
        if (!normalized) return;
        localById.set(normalized.id, normalized);
    });

    primary.forEach((item) => {
        const normalizedPrimary = normalizeRecord(item);
        if (!normalizedPrimary) return;

        const local = localById.get(normalizedPrimary.id);
        if (!local) {
            merged.set(normalizedPrimary.id, normalizedPrimary);
            return;
        }

        const joinedAt =
            toTimestamp(local.joinedAt) > toTimestamp(normalizedPrimary.joinedAt)
                ? local.joinedAt
                : normalizedPrimary.joinedAt;

        merged.set(normalizedPrimary.id, {
            ...local,
            ...normalizedPrimary,
            title: choosePreferredTitle(normalizedPrimary.title, local.title),
            joinedAt,
            playlistId: normalizeText(normalizedPrimary.playlistId || local.playlistId),
            hostId: normalizeText(normalizedPrimary.hostId || local.hostId),
            hostName: normalizeText(normalizedPrimary.hostName || local.hostName),
            hostAvatar: normalizeText(normalizedPrimary.hostAvatar || local.hostAvatar),
            isOnline:
                typeof normalizedPrimary.isOnline === "boolean"
                    ? normalizedPrimary.isOnline
                    : local.isOnline,
            songCount:
                typeof normalizedPrimary.songCount === "number"
                    ? normalizedPrimary.songCount
                    : local.songCount,
        });
        localById.delete(normalizedPrimary.id);
    });

    localById.forEach((item) => {
        merged.set(item.id, item);
    });

    return sortByMostRecent(Array.from(merged.values()));
};

const getGuestIdentityHeaders = (): Record<string, string> => {
    if (typeof window === "undefined") return {};
    const rawGuest = localStorage.getItem("guestUser");
    if (!rawGuest) return {};
    const guestIdPattern = /^guest_[a-z0-9][a-z0-9_-]{2,63}$/i;

    try {
        const parsed = JSON.parse(rawGuest);
        const rawGuestId = normalizeText(parsed?.id ? String(parsed.id) : "");
        const guestId = guestIdPattern.test(rawGuestId) ? rawGuestId : "";
        const guestName = normalizeText(parsed?.name ? String(parsed.name) : "");
        return {
            ...(guestId ? { "X-Guest-Id": guestId } : {}),
            ...(guestName ? { "X-Guest-Name": guestName } : {}),
        };
    } catch {
        return {};
    }
};

export const fetchRecentlyJoinedPlaylistsFromApi = async (
    limit?: number
): Promise<RecentlyJoinedPlaylistRecord[]> => {
    if (typeof window === "undefined") return [];
    if (isRateLimitCoolingDown(RECENT_JOINS_RATE_LIMIT_KEY)) {
        return getRecentlyJoinedPlaylists();
    }

    const token = getStoredAuthToken();
    const headers: Record<string, string> = token ? {} : getGuestIdentityHeaders();
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    const normalizedLimit =
        typeof limit === "number" && Number.isFinite(limit) && limit > 0
            ? Math.floor(limit)
            : null;
    const query = normalizedLimit ? `?limit=${normalizedLimit}` : "";
    const response = await fetch(`/api/v1/sessions/me/recent-joined-playlists${query}`, {
        headers,
    });

    if (response.status === 401 || response.status === 403) {
        return [];
    }
    if (response.status === 429) {
        setRateLimitCooldown(
            RECENT_JOINS_RATE_LIMIT_KEY,
            getRetryAfterSecondsFromHeaders(response.headers),
            60
        );
        return getRecentlyJoinedPlaylists();
    }
    if (!response.ok) {
        throw new Error(`Failed to fetch recent joins: ${response.status}`);
    }

    const payload = (await response.json()) as RecentlyJoinedPlaylistsApiResponse;
    const items = Array.isArray(payload?.items) ? payload.items : [];
    return sortByMostRecent(
        items
            .map((item) => {
                const rawTitle = normalizeText(
                    item.playlist_title || item.title || item.name,
                    "Untitled Playlist"
                );
                const safeTitle = isLikelyIdentifier(rawTitle)
                    ? `${normalizeText(item.host_name, "Host")}'s Session`
                    : choosePreferredTitle(rawTitle, item.title || item.name);

                return normalizeRecord({
                    id: item.session_id || item.id || "",
                    playlistId: item.playlist_id || "",
                    hostId: item.host_id || "",
                    hostName: item.host_name || "",
                    hostAvatar: item.host_avatar || "",
                    title: safeTitle,
                    image: item.image || "/placeholder.png",
                    joinedAt: item.joined_at || item.joinedAt || new Date().toISOString(),
                    source: "session",
                    isOnline: typeof item.is_online === "boolean" ? item.is_online : true,
                    songCount:
                        typeof item.song_count === "number" && Number.isFinite(item.song_count)
                            ? item.song_count
                            : undefined,
                });
            })
            .filter((item): item is RecentlyJoinedPlaylistRecord => Boolean(item))
    );
};

export const formatRelativeJoinedAt = (joinedAt: string, nowMs = Date.now()): string => {
    const time = new Date(joinedAt).getTime();
    if (Number.isNaN(time)) return "Recently";

    const deltaSeconds = Math.max(0, Math.floor((nowMs - time) / 1000));
    if (deltaSeconds < 60) return "Just now";

    const minutes = Math.floor(deltaSeconds / 60);
    if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"} ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;

    return new Date(joinedAt).toLocaleDateString();
};
